import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle, Upload, X, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ASSETS = {
  flexFlooring1: "https://customer-assets.emergentagent.com/job_garage-reset/artifacts/d9zikw23_Flex%20Flooring%201.jpeg",
  flexFlooring2: "https://customer-assets.emergentagent.com/job_garage-reset/artifacts/x6768j2k_Flex%20Fllooring%202.jpeg",
  flexFlooring3: "https://customer-assets.emergentagent.com/job_garage-reset/artifacts/i87nuj2p_Flex%20Flooring%203.jpeg",
  flexFlooring5: "https://customer-assets.emergentagent.com/job_garage-reset/artifacts/5ogjgwxd_Flex%20Flooring%205.jpeg",
  ceramicCoating1: "https://customer-assets.emergentagent.com/job_garage-reset/artifacts/l2v25es0_Cermaic%20Coating%201.png",
  ceramicCoating2: "https://customer-assets.emergentagent.com/job_garage-reset/artifacts/thzcpgsi_Cermaic%20Coating%202.png.jpeg",
  dashcam1: "https://customer-assets.emergentagent.com/job_garage-reset/artifacts/aimc6hno_Dashcam%201.jpeg",
  dashcam2: "https://customer-assets.emergentagent.com/job_garage-reset/artifacts/r1yvokmw_Dashcam%202.jpeg"
};

// Flex Flooring images array for carousel
const FLEX_FLOORING_IMAGES = [
  { src: ASSETS.flexFlooring1, alt: "Flex Flooring - Teal Tile Pattern" },
  { src: ASSETS.flexFlooring2, alt: "Flex Flooring - Installation View" },
  { src: ASSETS.flexFlooring3, alt: "Flex Flooring - Completed Garage" },
  { src: ASSETS.flexFlooring5, alt: "Flex Flooring - Premium Finish" }
];

// Ceramic Coating images array for carousel
const CERAMIC_COATING_IMAGES = [
  { src: ASSETS.ceramicCoating1, alt: "Ceramic Coating - Professional Application" },
  { src: ASSETS.ceramicCoating2, alt: "Ceramic Coating - Order & Clean Co Team" }
];

// Dashcam images array for carousel
const DASHCAM_IMAGES = [
  { src: ASSETS.dashcam1, alt: "Dashcam Installation - Professional Setup" },
  { src: ASSETS.dashcam2, alt: "Dashcam Installation - Order & Clean Co Team" }
];

const EnhancementsPage = () => {
  const [selectedEnhancement, setSelectedEnhancement] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [flooringSlide, setFlooringSlide] = useState(0);
  const [ceramicSlide, setCeramicSlide] = useState(0);
  const [dashcamSlide, setDashcamSlide] = useState(0);
  const [formData, setFormData] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    notes: ""
  });

  const nextFlooringSlide = () => {
    setFlooringSlide((prev) => (prev + 1) % FLEX_FLOORING_IMAGES.length);
  };

  const prevFlooringSlide = () => {
    setFlooringSlide((prev) => (prev - 1 + FLEX_FLOORING_IMAGES.length) % FLEX_FLOORING_IMAGES.length);
  };

  const nextCeramicSlide = () => {
    setCeramicSlide((prev) => (prev + 1) % CERAMIC_COATING_IMAGES.length);
  };

  const prevCeramicSlide = () => {
    setCeramicSlide((prev) => (prev - 1 + CERAMIC_COATING_IMAGES.length) % CERAMIC_COATING_IMAGES.length);
  };

  const nextDashcamSlide = () => {
    setDashcamSlide((prev) => (prev + 1) % DASHCAM_IMAGES.length);
  };

  const prevDashcamSlide = () => {
    setDashcamSlide((prev) => (prev - 1 + DASHCAM_IMAGES.length) % DASHCAM_IMAGES.length);
  };

  // Featured/Popular services
  const featuredEnhancements = [
    {
      id: "flooring",
      name: "Flex Garage Flooring",
      description: "Durable, high-end garage flooring designed to transform bare concrete into a clean, modern, and easy-to-maintain surface.",
      benefits: [
        "Professional garage transformation",
        "Durable and chemical resistant",
        "Slip-resistant surface",
        "Easy to clean and maintain",
        "Modern showroom-style finish"
      ],
      image: ASSETS.flexFlooring3,
      featured: true
    },
    {
      id: "ceramic_coating",
      name: "Ceramic Coating",
      description: "Premium ceramic coating protection for your vehicle. Long-lasting shine and protection against the elements.",
      benefits: [
        "Long-lasting paint protection",
        "Hydrophobic water-beading effect",
        "UV protection",
        "Enhanced gloss and shine",
        "Easier cleaning and maintenance"
      ],
      image: ASSETS.flexFlooring1,
      featured: true
    },
    {
      id: "dashcam",
      name: "Vehicle Dashcam Installation",
      description: "Professional dashcam installation for improved vehicle security and peace of mind on Sydney roads.",
      benefits: [
        "Hidden professional wiring",
        "Parking mode setup",
        "Front and rear coverage options",
        "GPS integration available",
        "Incident recording protection"
      ],
      image: null,
      featured: true
    }
  ];

  // Other enhancement services
  const otherEnhancements = [
    {
      id: "epoxy_flooring",
      name: "Epoxy Flooring",
      description: "High-performance epoxy floor coating for a seamless, durable garage surface.",
      benefits: [
        "Seamless finish",
        "Chemical and stain resistant",
        "Easy to clean",
        "Long-lasting durability"
      ],
      image: null
    },
    {
      id: "lighting",
      name: "Garage Lighting Installation",
      description: "Upgrade to brighter LED lighting for improved visibility and a cleaner workspace.",
      benefits: [
        "Energy-efficient LED technology",
        "Improved visibility for work",
        "Professional installation",
        "Long-lasting performance"
      ],
      image: null
    },
    {
      id: "electrical",
      name: "Electrical Outlet Installation",
      description: "Install additional outlets for tools, charging stations, or workbenches.",
      benefits: [
        "Licensed electrician service",
        "Strategic outlet placement",
        "Safety compliant installation",
        "Power for all your tools"
      ],
      image: null
    },
    {
      id: "shelving",
      name: "Storage Shelving Installation",
      description: "Custom shelving systems to maximise vertical storage and organisation.",
      benefits: [
        "Custom fitted to your space",
        "Heavy-duty construction",
        "Maximise vertical space",
        "Professional installation"
      ],
      image: null
    },
    {
      id: "house_tinting",
      name: "House Window Tinting",
      description: "Reduce heat, glare, and UV exposure while improving privacy and energy efficiency.",
      benefits: [
        "Reduce energy costs",
        "UV protection",
        "Improved privacy",
        "Professional application"
      ],
      image: null
    },
    {
      id: "car_tinting",
      name: "Car Window Tinting",
      description: "Professional automotive window tinting for heat reduction and privacy.",
      benefits: [
        "High-quality tint film",
        "Legal compliance",
        "Heat reduction",
        "UV protection"
      ],
      image: null
    },
    {
      id: "upholstery_cleaning",
      name: "Upholstery Cleaning & Restoration",
      description: "Professional upholstery cleaning and restoration to refresh worn furniture and bring it back to life.",
      benefits: [
        "Deep cleaning",
        "Stain removal",
        "Fabric restoration",
        "Odour elimination"
      ],
      image: null
    },
    {
      id: "carpet_cleaning",
      name: "Carpet Cleaning & Protection",
      description: "Professional carpet cleaning and protection services to remove dirt, stains, and allergens while extending carpet life.",
      benefits: [
        "Deep steam cleaning",
        "Stain and spot removal",
        "Protective treatment application",
        "Allergen elimination"
      ],
      image: null
    },
    // Vehicle Enhancement Services
    {
      id: "fire_extinguisher_firstaid",
      name: "Fire Extinguisher & First Aid Kit Installation",
      description: "Professional installation of fire extinguishers and first aid kits in cars and trucks for safety compliance and peace of mind.",
      benefits: [
        "Secure mounting",
        "Easy access placement",
        "Compliance ready",
        "Professional installation"
      ],
      image: null
    },
    {
      id: "sensors_installation",
      name: "Front & Rear Sensor Installation",
      description: "Professional installation of parking sensors for cars and trucks to improve safety and prevent accidents.",
      benefits: [
        "Front and rear coverage",
        "Audio and visual alerts",
        "Professional calibration",
        "Clean installation"
      ],
      image: null
    },
    {
      id: "sound_system",
      name: "Sound System Upgrades",
      description: "Upgrade your vehicle's audio with premium head units featuring Apple CarPlay and Android Auto integration.",
      benefits: [
        "Apple CarPlay integration",
        "Android Auto compatible",
        "Premium sound quality",
        "Professional installation"
      ],
      image: null
    },
    {
      id: "phone_range_extender",
      name: "Mobile Phone Range Extenders",
      description: "Boost your mobile signal with professional range extender installation for cars and trucks.",
      benefits: [
        "Improved signal strength",
        "Better call quality",
        "Faster data speeds",
        "Professional installation"
      ],
      image: null
    },
    {
      id: "spotlight_worklights",
      name: "Spotlight & Working Lights Installation",
      description: "Professional installation of spotlights and working lights for cars and trucks.",
      benefits: [
        "Improved visibility",
        "Quality LED options",
        "Secure mounting",
        "Professional wiring"
      ],
      image: null
    },
    // Cleaning Services
    {
      id: "tile_grout_cleaning",
      name: "Tile & Grout Cleaning",
      description: "Professional tile and grout cleaning to restore the original look of your floors and surfaces.",
      benefits: [
        "Deep grout cleaning",
        "Stain removal",
        "Restored appearance",
        "Protective sealing available"
      ],
      image: null
    },
    {
      id: "leather_cleaning",
      name: "Leather Cleaning",
      description: "Professional leather cleaning and conditioning for furniture and vehicle interiors.",
      benefits: [
        "Gentle deep cleaning",
        "Conditioning treatment",
        "Restored suppleness",
        "Protection application"
      ],
      image: null
    },
    {
      id: "rug_washing",
      name: "Rug Washing",
      description: "Professional rug washing service to deep clean and refresh your rugs.",
      benefits: [
        "Full immersion cleaning",
        "Stain treatment",
        "Odour removal",
        "Careful drying process"
      ],
      image: null
    },
    {
      id: "outdoor_surface_cleaning",
      name: "Outdoor Surface Cleaning",
      description: "Professional cleaning of outdoor surfaces including driveways, patios, and decks.",
      benefits: [
        "High-pressure cleaning",
        "Moss and algae removal",
        "Restored appearance",
        "Surface protection options"
      ],
      image: null
    },
    {
      id: "stone_restoration",
      name: "Premium Stone Restoration & Sealing",
      description: "Expert restoration and sealing of natural stone surfaces to protect and enhance their beauty.",
      benefits: [
        "Professional restoration",
        "Scratch and stain repair",
        "Premium sealing",
        "Long-lasting protection"
      ],
      image: null
    },
    // Home Services
    {
      id: "home_network",
      name: "Home Network Upgrade",
      description: "Let our IT specialist upgrade and organise your existing network infrastructure to provide reliable, secure and scalable connectivity for all your devices.",
      benefits: [
        "Custom network rack setup",
        "Equipment installation & config",
        "Professional cable management",
        "Secure, scalable connectivity"
      ],
      image: null
    }
  ];

  const handleRequestQuote = (enhancement) => {
    setSelectedEnhancement(enhancement);
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/enhancement-leads`, {
        enhancement_type: selectedEnhancement.id,
        ...formData
      });

      toast.success("Quote request submitted!", {
        description: "We'll be in touch soon with your quote."
      });

      setIsDialogOpen(false);
      setFormData({
        customer_name: "",
        customer_email: "",
        customer_phone: "",
        notes: ""
      });
    } catch (error) {
      toast.error("Failed to submit request", {
        description: "Please try again or contact us directly."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="section-padding bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="caption text-slate-400 block mb-4">Space Enhancements</span>
              <h1 className="heading-xl text-white mb-6" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Upgrade Your<br />
                <span className="text-slate-300">Space</span>
              </h1>
              <p className="body-lg text-slate-400 mb-8">
                Transform your garage from a storage area into a clean, functional, 
                and premium space with our range of upgrade services.
              </p>
              <Link to="/booking">
                <Button className="btn-primary" data-testid="enhancements-cta">
                  Book Garage Service First
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img 
                src={ASSETS.flexFlooring5}
                alt="Premium garage flooring"
                className="w-full aspect-[4/3] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Popular Services - Featured */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="caption text-[#1a365d] block mb-4">Popular Services</span>
            <h2 className="heading-lg text-[#0A0A0A]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Most Requested Upgrades
            </h2>
          </div>

          {/* Flex Flooring with Carousel */}
          <div className="mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Image Carousel */}
              <div className="relative">
                <div className="relative overflow-hidden">
                  <img 
                    src={FLEX_FLOORING_IMAGES[flooringSlide].src}
                    alt={FLEX_FLOORING_IMAGES[flooringSlide].alt}
                    className="w-full aspect-[4/3] object-cover transition-opacity duration-300"
                  />
                  <div className="absolute top-4 left-4 bg-[#1a365d] text-white px-4 py-2">
                    <span className="font-bold uppercase text-sm tracking-wider">Popular</span>
                  </div>
                  
                  {/* Carousel Navigation Arrows */}
                  <button 
                    onClick={prevFlooringSlide}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                    data-testid="flooring-prev"
                  >
                    <ChevronLeft size={24} />
                  </button>
                  <button 
                    onClick={nextFlooringSlide}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                    data-testid="flooring-next"
                  >
                    <ChevronRight size={24} />
                  </button>
                </div>
                
                {/* Thumbnail Navigation */}
                <div className="flex gap-2 mt-4 justify-center">
                  {FLEX_FLOORING_IMAGES.map((img, i) => (
                    <button
                      key={i}
                      onClick={() => setFlooringSlide(i)}
                      className={`w-16 h-16 overflow-hidden border-2 transition-colors ${
                        flooringSlide === i ? 'border-[#1a365d]' : 'border-transparent opacity-60 hover:opacity-100'
                      }`}
                    >
                      <img src={img.src} alt={img.alt} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
                <p className="text-center text-sm text-slate-500 mt-2">
                  {flooringSlide + 1} / {FLEX_FLOORING_IMAGES.length} - Click thumbnails or arrows to browse
                </p>
              </div>

              {/* Content */}
              <div>
                <h2 className="heading-lg text-[#0A0A0A] mb-4" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  Flex Garage Flooring
                </h2>
                <p className="body-lg text-slate-600 mb-8">
                  Durable, high-end garage flooring designed to transform bare concrete into a clean, modern, and easy-to-maintain surface.
                </p>
                <ul className="space-y-3 mb-8">
                  {[
                    "Professional garage transformation",
                    "Durable and chemical resistant",
                    "Slip-resistant surface",
                    "Easy to clean and maintain",
                    "Modern showroom-style finish"
                  ].map((benefit, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-700">
                      <CheckCircle size={20} className="text-green-500" />
                      {benefit}
                    </li>
                  ))}
                </ul>
                <Button 
                  className="btn-primary"
                  onClick={() => handleRequestQuote(featuredEnhancements[0])}
                  data-testid="request-flooring-quote"
                >
                  Request Quote
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </div>
            </div>
          </div>

          {/* Ceramic Coating with Carousel */}
          <div className="mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Content first on this one */}
              <div>
                <h2 className="heading-lg text-[#0A0A0A] mb-4" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  Ceramic Coating
                </h2>
                <p className="body-lg text-slate-600 mb-8">
                  Premium ceramic coating protection for your vehicle. Long-lasting shine and protection against the elements.
                </p>
                <ul className="space-y-3 mb-8">
                  {[
                    "Long-lasting paint protection",
                    "Hydrophobic water-beading effect",
                    "UV protection",
                    "Enhanced gloss and shine",
                    "Easier cleaning and maintenance"
                  ].map((benefit, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-700">
                      <CheckCircle size={20} className="text-green-500" />
                      {benefit}
                    </li>
                  ))}
                </ul>
                <Button 
                  className="btn-primary"
                  onClick={() => handleRequestQuote(featuredEnhancements[1])}
                  data-testid="request-ceramic-quote"
                >
                  Request Quote
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </div>

              {/* Image Carousel */}
              <div className="relative">
                <div className="relative overflow-hidden">
                  <img 
                    src={CERAMIC_COATING_IMAGES[ceramicSlide].src}
                    alt={CERAMIC_COATING_IMAGES[ceramicSlide].alt}
                    className="w-full aspect-[4/3] object-cover transition-opacity duration-300"
                  />
                  <div className="absolute top-4 left-4 bg-[#1a365d] text-white px-4 py-2">
                    <span className="font-bold uppercase text-sm tracking-wider">Popular</span>
                  </div>
                  
                  {/* Carousel Navigation Arrows */}
                  <button 
                    onClick={prevCeramicSlide}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                    data-testid="ceramic-prev"
                  >
                    <ChevronLeft size={24} />
                  </button>
                  <button 
                    onClick={nextCeramicSlide}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                    data-testid="ceramic-next"
                  >
                    <ChevronRight size={24} />
                  </button>
                </div>
                
                {/* Thumbnail Navigation */}
                <div className="flex gap-2 mt-4 justify-center">
                  {CERAMIC_COATING_IMAGES.map((img, i) => (
                    <button
                      key={i}
                      onClick={() => setCeramicSlide(i)}
                      className={`w-16 h-16 overflow-hidden border-2 transition-colors ${
                        ceramicSlide === i ? 'border-[#1a365d]' : 'border-transparent opacity-60 hover:opacity-100'
                      }`}
                    >
                      <img src={img.src} alt={img.alt} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
                <p className="text-center text-sm text-slate-500 mt-2">
                  {ceramicSlide + 1} / {CERAMIC_COATING_IMAGES.length} - Click thumbnails or arrows to browse
                </p>
              </div>
            </div>
          </div>

          {/* Vehicle Dashcam with Carousel */}
          <div className="mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Image Carousel */}
              <div className="relative">
                <div className="relative overflow-hidden">
                  <img 
                    src={DASHCAM_IMAGES[dashcamSlide].src}
                    alt={DASHCAM_IMAGES[dashcamSlide].alt}
                    className="w-full aspect-[4/3] object-cover transition-opacity duration-300"
                  />
                  <div className="absolute top-4 left-4 bg-[#1a365d] text-white px-4 py-2">
                    <span className="font-bold uppercase text-sm tracking-wider">Popular</span>
                  </div>
                  
                  {/* Carousel Navigation Arrows */}
                  <button 
                    onClick={prevDashcamSlide}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                    data-testid="dashcam-prev"
                  >
                    <ChevronLeft size={24} />
                  </button>
                  <button 
                    onClick={nextDashcamSlide}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                    data-testid="dashcam-next"
                  >
                    <ChevronRight size={24} />
                  </button>
                </div>
                
                {/* Thumbnail Navigation */}
                <div className="flex gap-2 mt-4 justify-center">
                  {DASHCAM_IMAGES.map((img, i) => (
                    <button
                      key={i}
                      onClick={() => setDashcamSlide(i)}
                      className={`w-16 h-16 overflow-hidden border-2 transition-colors ${
                        dashcamSlide === i ? 'border-[#1a365d]' : 'border-transparent opacity-60 hover:opacity-100'
                      }`}
                    >
                      <img src={img.src} alt={img.alt} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
                <p className="text-center text-sm text-slate-500 mt-2">
                  {dashcamSlide + 1} / {DASHCAM_IMAGES.length} - Click thumbnails or arrows to browse
                </p>
              </div>

              {/* Content */}
              <div>
                <h2 className="heading-lg text-[#0A0A0A] mb-4" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  Vehicle Dashcam Installation
                </h2>
                <p className="body-lg text-slate-600 mb-8">
                  Professional dashcam installation for improved vehicle security and peace of mind on Sydney roads.
                </p>
                <ul className="space-y-3 mb-8">
                  {[
                    "Hidden professional wiring",
                    "Parking mode setup",
                    "Front and rear coverage options",
                    "GPS integration available",
                    "Incident recording protection"
                  ].map((benefit, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-700">
                      <CheckCircle size={20} className="text-green-500" />
                      {benefit}
                    </li>
                  ))}
                </ul>
                <Button 
                  className="btn-primary"
                  onClick={() => handleRequestQuote(featuredEnhancements[2])}
                  data-testid="request-dashcam-quote"
                >
                  Request Quote
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Other Enhancement Services */}
      <section className="section-padding bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="caption text-[#1a365d] block mb-4">More Services</span>
            <h2 className="heading-lg text-[#0A0A0A]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Other Enhancement Services
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {otherEnhancements.map((enhancement) => (
              <div 
                key={enhancement.id}
                className="bg-white p-6 border border-slate-200 hover:border-[#1a365d]/50 transition-colors"
                data-testid={`enhancement-${enhancement.id}`}
              >
                {enhancement.image && (
                  <img 
                    src={enhancement.image}
                    alt={enhancement.name}
                    className="w-full h-48 object-cover mb-6"
                  />
                )}
                <h3 className="heading-md text-[#0A0A0A] mb-3" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  {enhancement.name}
                </h3>
                <p className="text-slate-600 mb-4">
                  {enhancement.description}
                </p>
                <ul className="space-y-2 mb-6">
                  {enhancement.benefits.slice(0, 3).map((benefit, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-700">
                      <CheckCircle size={14} className="text-green-500" />
                      {benefit}
                    </li>
                  ))}
                </ul>
                <Button 
                  variant="outline"
                  className="w-full btn-secondary"
                  onClick={() => handleRequestQuote(enhancement)}
                  data-testid={`request-${enhancement.id}-quote`}
                >
                  Request Quote
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="caption text-[#1a365d] block mb-4">Process</span>
            <h2 className="heading-lg text-[#0A0A0A]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              How It Works
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: "01",
                title: "Complete Garage Service",
                description: "First, book and complete your Garage Clear-Out or Reset service."
              },
              {
                step: "02",
                title: "Request Enhancement",
                description: "Select your desired upgrades and submit a quote request."
              },
              {
                step: "03",
                title: "We Handle Everything",
                description: "Our team will contact you to arrange the service and complete the upgrade."
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="relative p-8 border-l-4 border-[#1a365d] bg-slate-50"
              >
                <span className="text-6xl font-bold text-slate-200 absolute top-4 right-4" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  {item.step}
                </span>
                <h3 className="heading-md text-[#0A0A0A] mb-4 relative z-10" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  {item.title}
                </h3>
                <p className="text-slate-600 relative z-10">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Flooring Gallery */}
      <section className="section-padding bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="caption text-slate-400 block mb-4">Gallery</span>
            <h2 className="heading-lg text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Flex Flooring Transformations
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[ASSETS.flexFlooring1, ASSETS.flexFlooring2, ASSETS.flexFlooring3, ASSETS.flexFlooring5].map((img, i) => (
              <div key={i} className="aspect-square overflow-hidden">
                <img 
                  src={img}
                  alt={`Flex Flooring Example ${i + 1}`}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-[#1a365d]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="heading-lg text-white mb-6" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Start With a Garage Service
          </h2>
          <p className="body-lg text-slate-300 mb-8">
            Book your garage clear-out or reset first, then add enhancements 
            to complete your transformation.
          </p>
          <Link to="/booking" data-testid="enhancements-book-cta">
            <Button className="bg-white text-[#1a365d] hover:bg-slate-100 px-8 py-4 uppercase font-bold tracking-wider text-sm transition-all">
              Book Garage Service
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
        </div>
      </section>

      {/* Quote Request Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="heading-md" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Request {selectedEnhancement?.name} Quote
            </DialogTitle>
            <DialogDescription>
              Fill in your details and we'll be in touch with a quote.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Name</label>
              <Input
                type="text"
                required
                value={formData.customer_name}
                onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                className="input-field"
                placeholder="Your name"
                data-testid="quote-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <Input
                type="email"
                required
                value={formData.customer_email}
                onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                className="input-field"
                placeholder="your@email.com"
                data-testid="quote-email-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Phone</label>
              <Input
                type="tel"
                required
                value={formData.customer_phone}
                onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                className="input-field"
                placeholder="0400 000 000"
                data-testid="quote-phone-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Notes (Optional)</label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="input-field min-h-[100px]"
                placeholder="Any additional details about your requirements..."
                data-testid="quote-notes-input"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full btn-primary"
              disabled={isSubmitting}
              data-testid="submit-quote-btn"
            >
              {isSubmitting ? "Submitting..." : "Submit Quote Request"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EnhancementsPage;
